import { readFile, access, readdir } from 'node:fs/promises';
import { join } from 'node:path';
import type { Preset } from '../types/index.js';

export interface DetectedStack {
  preset: Preset;
  confidence: number;
  language: 'typescript' | 'javascript' | 'python' | 'unknown';
  framework?: string;
  packageManager?: 'npm' | 'yarn' | 'pnpm' | 'bun';
  hasTests: boolean;
  hasTypeScript: boolean;
  hasCi: boolean;
  issues: string[];
}

export async function detectStack(cwd: string): Promise<DetectedStack> {
  const result: DetectedStack = {
    preset: 'node-api',
    confidence: 0,
    language: 'unknown',
    hasTests: false,
    hasTypeScript: false,
    hasCi: false,
    issues: [],
  };

  const files = await safeReadDir(cwd);

  // Detect package manager
  if (files.includes('package-lock.json')) {
    result.packageManager = 'npm';
  } else if (files.includes('yarn.lock')) {
    result.packageManager = 'yarn';
  } else if (files.includes('pnpm-lock.yaml')) {
    result.packageManager = 'pnpm';
  } else if (files.includes('bun.lockb')) {
    result.packageManager = 'bun';
  }

  // Detect TypeScript
  if (files.includes('tsconfig.json')) {
    result.hasTypeScript = true;
    result.language = 'typescript';
  }

  // Detect CI
  if (files.includes('.github')) {
    const githubFiles = await safeReadDir(join(cwd, '.github'));
    if (githubFiles.includes('workflows')) {
      result.hasCi = true;
    }
  }
  if (files.includes('.gitlab-ci.yml')) {
    result.hasCi = true;
  }

  // Detect tests
  const testIndicators = ['test', 'tests', '__tests__', 'spec', 'specs', 'vitest.config.ts', 'jest.config.js'];
  result.hasTests = testIndicators.some((t) => files.includes(t));

  // Detect framework from package.json
  if (files.includes('package.json')) {
    result.language = result.hasTypeScript ? 'typescript' : 'javascript';
    const pkg = await readPackageJson(cwd);

    if (pkg) {
      const allDeps = { ...pkg.dependencies, ...pkg.devDependencies };

      // Next.js detection
      if (allDeps['next']) {
        result.preset = 'nextjs-app';
        result.framework = 'next.js';
        result.confidence = 95;
      }
      // Express/Fastify/Koa = node-api
      else if (allDeps['express'] || allDeps['fastify'] || allDeps['koa'] || allDeps['hono']) {
        result.preset = 'node-api';
        result.framework = allDeps['express']
          ? 'express'
          : allDeps['fastify']
            ? 'fastify'
            : allDeps['koa']
              ? 'koa'
              : 'hono';
        result.confidence = 90;
      }
      // Library detection (has main/exports, no framework)
      else if (pkg.main || pkg.exports) {
        result.preset = 'library';
        result.confidence = 70;
      }
      // Default to node-api
      else {
        result.preset = 'node-api';
        result.confidence = 50;
      }

      // Check for monorepo
      if (pkg.workspaces || files.includes('lerna.json') || files.includes('pnpm-workspace.yaml')) {
        result.preset = 'monorepo';
        result.confidence = 85;
      }
    }
  }

  // Python detection
  if (files.includes('requirements.txt') || files.includes('pyproject.toml') || files.includes('setup.py')) {
    result.language = 'python' as any;
    result.preset = 'python-api';
    result.confidence = 80;

    if (files.includes('pyproject.toml')) {
      const content = await safeReadFile(join(cwd, 'pyproject.toml'));
      if (content?.includes('fastapi')) {
        result.framework = 'fastapi';
        result.confidence = 95;
      } else if (content?.includes('django')) {
        result.framework = 'django';
        result.confidence = 95;
      } else if (content?.includes('flask')) {
        result.framework = 'flask';
        result.confidence = 95;
      }
    }
  }

  // Collect issues for "wow moment"
  await collectIssues(cwd, files, result);

  return result;
}

async function collectIssues(cwd: string, files: string[], result: DetectedStack): Promise<void> {
  // Check for missing .gitignore
  if (!files.includes('.gitignore')) {
    result.issues.push('Missing .gitignore file');
  } else {
    const gitignore = await safeReadFile(join(cwd, '.gitignore'));
    if (gitignore && !gitignore.includes('.env')) {
      result.issues.push('.gitignore missing .env pattern');
    }
  }

  // Check for .env files that might be committed
  const envFiles = files.filter((f) => f.startsWith('.env') && !f.endsWith('.example'));
  if (envFiles.length > 0) {
    result.issues.push(`Potential secrets: ${envFiles.join(', ')}`);
  }

  // Check for lockfile
  if (result.language !== 'python' && !result.packageManager) {
    result.issues.push('No lockfile found (npm/yarn/pnpm)');
  }

  // Check for tests
  if (!result.hasTests) {
    result.issues.push('No test files detected');
  }

  // Check for TypeScript strict mode
  if (result.hasTypeScript) {
    const tsconfig = await safeReadFile(join(cwd, 'tsconfig.json'));
    if (tsconfig && !tsconfig.includes('"strict": true')) {
      result.issues.push('TypeScript strict mode not enabled');
    }
  }

  // Check for CI
  if (!result.hasCi) {
    result.issues.push('No CI/CD configuration found');
  }
}

async function safeReadDir(path: string): Promise<string[]> {
  try {
    return await readdir(path);
  } catch {
    return [];
  }
}

async function safeReadFile(path: string): Promise<string | null> {
  try {
    return await readFile(path, 'utf-8');
  } catch {
    return null;
  }
}

async function readPackageJson(cwd: string): Promise<any> {
  try {
    const content = await readFile(join(cwd, 'package.json'), 'utf-8');
    return JSON.parse(content);
  } catch {
    return null;
  }
}
